export type MarketingPerformanceAnalysisCustomer = {
  hasAutomatedCSVUploads: boolean;
  safeAnalysisDateRangeStart: string;
  googleAdsDestId?: string;
  metaAdsDestIds?: string[];
  bingAdsDestId?: string;
};

export type SortField =
  | "campaignName"
  | "campaignSpend"
  | "campaignStatus"
  | "revenue"
  | "returnOnAdSpend"
  | "numConversions"
  | "customerAcquisitionCost"
  | "successRate"
  | "totalBooked"
  | "totalAttended"
  | "avgClickToBooked"
  | "avgBookedToAttended"
  | "avgClickToAttended";

export type MarketingMetricValue = {
  current: number;
  prev: number | null;
};

export enum FunnelConversionType {
  BOOKED = "booked",
  ATTENDED = "attended",
}

export enum CampaignStatus {
  ENABLED = "ENABLED",
  PAUSED = "PAUSED",
  DISABLED = "DISABLED",
  UNKNOWN = "UNKNOWN",
}

export type CampaignMetricsForFunnelConversions = {
  conversionType: string;
  numConversions: MarketingMetricValue;
  // customerAcquisitionCost is the cost to acquire a single customer in dollars
  customerAcquisitionCost: MarketingMetricValue;
  // revenue is the total revenue generated from the conversions in dollars
  revenue: MarketingMetricValue;
  // returnOnAdSpend is a percentage value ranging from 0 to 100%
  returnOnAdSpend: MarketingMetricValue;
};

export type MarketingPerformanceAnalysisResult = {
  campaignName: string;
  campaignId: string;
  campaignStatus: string;
  // campaignSpend is the total amount spent on the campaign in dollars
  campaignSpend: MarketingMetricValue;
  funnel: CampaignMetricsForFunnelConversions[];
  hasBookedRevenue: boolean;
  hasAttendedRevenue: boolean;
};

export type MarketingPerformanceAnalysisResponse =
  MarketingPerformanceAnalysisResult[];

export enum AppointmentStatus {
  NONE = "None",
  BOOKED = "Booked",
  ATTENDED = "Attended",
}

export function getAppointmentStatus(
  device: CampaignDevice,
): AppointmentStatus {
  if (device.attendedAppointment) {
    return AppointmentStatus.ATTENDED;
  }
  if (device.bookedAppointment) {
    return AppointmentStatus.BOOKED;
  }
  return AppointmentStatus.NONE;
}

export type CampaignDevice = {
  deviceId: string;
  lastEventTime: string;
  bookedAppointment: boolean;
  attendedAppointment: boolean;
};

type CampaignDevices = CampaignDevice[];

export type GetDevicesForCampaignsResponse = {
  pagination: {
    currentPage: number;
    nextPage: number;
    prevPage?: number;
    totalPages: number;
    totalRecords: number;
  };
  devices: CampaignDevices;
};

export type DeviceEvent = {
  insertId?: string;
  event: string;
  eventTime: string;
  properties: Record<string, any>;
};

export type DeviceEvents = DeviceEvent[];

export type GetDeviceEventStreamResponse = {
  events: DeviceEvents;
};

export type EventStreamDrawerParamState = {
  campaignID?: string;
  deviceID?: string;
  eventID?: string;
};

type CampaignMetricTimeSeries = {
  periodStart: string;
  value: number;
};

export enum CampaignTimeSeriesMetricName {
  ROAS = "ROAS",
  ATTENDED_APPOINTMENTS = "Attended Appointments",
  CAC = "Acquisition Cost",
}

export enum CampaignMetricType {
  Percentage = "percentage",
  Dollar = "dollar",
  Count = "count",
}

export type CampaignMetric = {
  name: CampaignTimeSeriesMetricName;
  currentTotal: number;
  previousTotal?: number;
  type: CampaignMetricType;
  timeSeries: CampaignMetricTimeSeries[];
};

export type CampaignMetrics = CampaignMetric[];

export enum CampaignMetricTimeSeriesInterval {
  Weekly = "WEEKLY",
  Monthly = "MONTHLY",
}

export type GetCampaignAnalysisTimeSeriesResponse = {
  campaignId: string;
  campaignName: string;
  metrics: CampaignMetrics;
};

export enum AdPlatformType {
  google_ads = "google_ads",
  meta_ads = "meta_ads",
}

export type AppointmentTimelineEntry = {
  externalId: string;
  clickDate: string;
  bookedAt: string;
  attendedAt: string | null;
  campaignName: string;
  // Whether the entry counts toward each aggregated funnel stage; treat
  // absent as counted.
  countsAsBooked?: boolean;
  countsAsAttended?: boolean;
};

export type GetAppointmentTimelineResponse = {
  appointments: AppointmentTimelineEntry[];
};
